import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { TYPE_BY_KEY } from '../lib/requests';
import Icon from './Icon';
import SolvedPicker from './SolvedPicker';

/* Your open requests — everything you posted to achieve something, until you
   mark it solved. One tidy line each (open it, see the replies); marking it
   solved or keeping an old one open lives in its ⋯ menu. A plain Post never
   appears here. */

function RowMenu({ r, onSolve, onKeep, onDismiss }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  const pick = (fn) => () => { setOpen(false); fn(); };
  return (
    <div className="orx-menu" ref={ref}>
      <button type="button" className="orx-more" aria-label={`Options for ${r.title}`} aria-expanded={open} onClick={() => setOpen(!open)}>⋯</button>
      {open && (
        <div className="orx-pop" role="menu">
          {r.state === 'not_approved' ? (
            <button type="button" role="menuitem" onClick={pick(onDismiss)}>Dismiss</button>
          ) : (<>
            <button type="button" role="menuitem" onClick={pick(onSolve)}>✓ Mark solved</button>
            {r.needs_nudge && <button type="button" role="menuitem" onClick={pick(onKeep)}>Keep open 30 more days</button>}
          </>)}
        </div>
      )}
    </div>
  );
}

export default function OpenRequests({ refresh, onOpen, notify, onChanged, onViewAll, className = '' }) {
  const [rows, setRows] = useState(null);
  const [solving, setSolving] = useState(null);

  const load = useCallback(() => {
    return api.myRequests().then(setRows).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load, refresh]);

  async function act(fn, id, message) {
    try {
      await fn(id);
      notify?.(message);
      load();
      onChanged?.();
    } catch (e) {
      notify?.(e.message);
    }
  }

  if (!rows) return null;
  const active = rows.filter((r) => r.state !== 'not_approved').length;
  return (
    <section className={`card rail-card open-requests ${className}`} aria-label="Your open requests">
      <div className="rail-head static">
        <span>Open Requests ({active})</span>
        {onViewAll && <button type="button" className="linklike rail-viewall" onClick={onViewAll}>View all <span aria-hidden="true">→</span></button>}
      </div>
      {rows.length === 0 && (
        <p className="rail-empty">Nothing open. Post an Ask, Test, Offer, Intro, Looking For or Refer — it stays here until you mark it solved.</p>
      )}
      <ul className="orx-list">
        {rows.map((r) => {
          const t = TYPE_BY_KEY[r.kind] || TYPE_BY_KEY.ask;
          const rejected = r.state === 'not_approved';
          const meta = r.kind === 'refer' ? `${r.referral_count} referred` : `${r.replies} repl${r.replies === 1 ? 'y' : 'ies'}`;
          const pill = rejected ? 'not approved' : r.state === 'pending' ? 'awaiting approval' : r.needs_nudge ? 'still open?' : 'open';
          return (
            <li key={r.id} className={`orx k-${r.kind}${rejected ? ' rejected' : ''}`}>
              <div className="orx-row">
                <button type="button" className="orx-main" onClick={() => !rejected && onOpen?.(r.id)}
                  title={rejected ? (r.fields?.review_reason || 'An admin couldn’t approve this reward.') : r.title}>
                  <span className="orx-icon" aria-label={t.label.replace('…', '')}><Icon name={t.icon} size={18} /></span>
                  <span className="orx-text">
                    <span className="orx-title">{r.title}</span>
                    <span className="orx-meta">{meta}</span>
                  </span>
                  <span className={`orx-pill${r.needs_nudge || rejected ? ' warn' : ''}`}>{pill}</span>
                </button>
                {r.state !== 'pending' && (
                  <RowMenu r={r}
                    onSolve={() => setSolving(solving === r.id ? null : r.id)}
                    onKeep={() => act(api.reopenRequest, r.id, 'Kept open for another 30 days')}
                    onDismiss={() => act(api.reviewSeen, r.id, 'Dismissed')} />
                )}
              </div>
              {rejected && <p className="orx-note">{r.fields?.review_reason || 'An admin couldn’t approve this reward.'} You can post it again without a reward.</p>}
              {solving === r.id && (
                <SolvedPicker postId={r.id} notify={notify} onCancel={() => setSolving(null)}
                  onDone={() => { setSolving(null); load(); onChanged?.(); }} />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
